/**
 * Engine pre-warm: a few seconds after the first stage mounts, fetch and
 * evaluate the engines' modules in story order (overview diorama, Cesium globe,
 * twin, then the GAIA column), one per idle period, so a later mode switch or
 * the first GAIA open only mounts them. The specifiers match the lazy imports in
 * CenterStage.tsx, LoaderGlobe.tsx and Shell.tsx, so
 * they resolve to the same chunks (served from the service worker's precache
 * once it is installed). Production only: in dev each import is a module graph
 * transform, which would slow the dev server for little gain.
 */
const STORY_ORDER: ReadonlyArray<readonly [name: string, load: () => Promise<unknown>]> = [
  ['overview diorama', () => import('@/features/overview/OverviewStage')],
  ['Cesium globe', () => import('@/features/loader/globe/CesiumGlobe')],
  ['twin', () => import('@/features/twin/scene/TwinStage')],
  ['GAIA column', () => import('@/features/gaia/GaiaColumn')],
];

/** Leaves the current mode its own start-up (stage, data, first frames) before pre-warming. */
const START_DELAY_MS = 3000;
const IDLE_TIMEOUT_MS = 2000;

let started = false;

function idle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(resolve, 200);
    }
  });
}

export function prewarmEngines(): void {
  if (started || !import.meta.env.PROD) return;
  started = true;
  window.setTimeout(() => {
    void (async () => {
      for (const [name, load] of STORY_ORDER) {
        await idle();
        try {
          await load();
        } catch (error) {
          // The mode's own lazy import retries and fails loudly in place; this keeps the record.
          console.error(`[prewarm] the ${name} module failed to load:`, error);
        }
      }
    })();
  }, START_DELAY_MS);
}
